/**
 * OutpostRepository — OutpostForge frontend (CES standard)
 * Repository pattern with injected service (DIP): hooks depend on this
 * abstraction, the concrete HTTP service is swappable (tests, mock data).
 * Mirrors the client's AccountRepository/AccountRepositoryFactory pattern.
 */

import type { OutpostService } from '../services/api/outpostService';
import { outpostService } from '../services/api/outpostService';
import type {
  AssetSummary,
  InstanceSummary,
  InstanceTypeItem,
  OutpostSummary,
} from '../types/outposts';

export class OutpostRepository {
  constructor(private readonly service: OutpostService) {}

  async getAllOutposts(): Promise<OutpostSummary[]> {
    const response = await this.service.getOutposts();
    return response.outposts;
  }

  async getAssets(outpostId: string): Promise<AssetSummary[]> {
    const response = await this.service.getAssets(outpostId);
    return response.assets;
  }

  async getInstances(outpostId: string, assetId?: string): Promise<InstanceSummary[]> {
    const response = await this.service.getInstances(outpostId, assetId);
    return response.instances;
  }

  /** Instances grouped by assetId (inventory occupancy view). */
  async getInstancesByAsset(outpostId: string): Promise<Map<string, InstanceSummary[]>> {
    const instances = await this.getInstances(outpostId);
    const grouped = new Map<string, InstanceSummary[]>();
    for (const instance of instances) {
      const list = grouped.get(instance.assetId) ?? [];
      list.push(instance);
      grouped.set(instance.assetId, list);
    }
    return grouped;
  }

  async getInstanceTypes(
    outpostId: string,
    assetId?: string,
  ): Promise<{ configured: InstanceTypeItem[]; supported?: InstanceTypeItem[] }> {
    const response = await this.service.getInstanceTypes(outpostId, assetId);
    return { configured: response.configured, supported: response.supported };
  }
}

export const OutpostRepositoryFactory = {
  create(service: OutpostService = outpostService): OutpostRepository {
    return new OutpostRepository(service);
  },
};

/** Default instance used by the hooks. */
export const outpostRepository = OutpostRepositoryFactory.create();
